import React from 'react';

function PrivacyPolicy() {
  return (
    <div
      className="container"
      style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}
      data-testid="privacy-policy"
    >
      <h1 className="heading-2" style={{ marginBottom: '32px' }}>
        Privacy Policy
      </h1>

      <p
        className="body-medium"
        style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}
      >
        Last updated: February 2026
      </p>

      {/* 1. Introduction */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          1. Introduction
        </h2>
        <p
          className="body-medium"
          style={{ lineHeight: '1.7', color: 'var(--text-secondary)' }}
        >
          Welcome to HealthTrail ("we," "our," or "us"). We are committed to
          protecting your personal information and your right to privacy. This
          Privacy Policy explains how we collect, use, disclose, and safeguard
          your information when you use our fitness and nutrition tracking
          application.
        </p>
      </section>

      {/* 2. Information We Collect */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          2. Information We Collect
        </h2>
        <p
          className="body-medium"
          style={{
            lineHeight: '1.7',
            color: 'var(--text-secondary)',
            marginBottom: '16px',
          }}
        >
          We collect information that you provide directly to us, including:
        </p>
        <ul
          style={{
            paddingLeft: '24px',
            color: 'var(--text-secondary)',
            lineHeight: '1.8',
          }}
        >
          <li>
            <strong>Account Information:</strong> Name, email address, and
            password when you register
          </li>
          <li>
            <strong>Profile Data:</strong> Age, gender, height, weight, activity
            level, and fitness goals
          </li>
          <li>
            <strong>Health &amp; Fitness Data:</strong> Food logs, exercise logs,
            calorie intake, nutritional information
          </li>
          <li>
            <strong>Photos:</strong> Food images you upload for AI analysis
          </li>
          <li>
            <strong>Wearable Data:</strong> Activity data synced from Fitbit,
            Oura Ring, Apple Health, or Samsung Health (with your permission)
          </li>
          <li>
            <strong>Communications:</strong> Messages sent through our platform
          </li>
        </ul>
      </section>

      {/* 3. How We Use Information */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          3. How We Use Your Information
        </h2>
        <ul
          style={{
            paddingLeft: '24px',
            color: 'var(--text-secondary)',
            lineHeight: '1.8',
          }}
        >
          <li>Provide, maintain, and improve our services</li>
          <li>Calculate calorie targets and track progress</li>
          <li>Analyze food images using AI for nutrition estimates</li>
          <li>Provide personalized fitness and wellness guidance</li>
          <li>Sync and display wearable device data</li>
          <li>Send notifications related to goals and activity</li>
          <li>Ensure platform security and reliability</li>
        </ul>
      </section>

      {/* 4. Advertising ID */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          4. Advertising ID Usage
        </h2>
        <p
          className="body-medium"
          style={{ lineHeight: '1.7', color: 'var(--text-secondary)' }}
        >
          <strong>HealthTrail does NOT use the Android Advertising ID (AAID).</strong>
        </p>
        <ul
          style={{
            paddingLeft: '24px',
            color: 'var(--text-secondary)',
            lineHeight: '1.8',
            marginTop: '12px',
          }}
        >
          <li>We do not collect or access the Advertising ID</li>
          <li>We do not display advertisements</li>
          <li>We do not perform ad attribution</li>
          <li>We do not track users across apps or websites for advertising</li>
        </ul>
        <p
          className="body-medium"
          style={{
            lineHeight: '1.7',
            color: 'var(--text-secondary)',
            marginTop: '12px',
          }}
        >
          Because HealthTrail does not use advertising technologies, no
          advertising identifiers are collected or shared.
        </p>
      </section>

      {/* 5. Third-Party Services */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          5. Third-Party Services
        </h2>
        <ul
          style={{
            paddingLeft: '24px',
            color: 'var(--text-secondary)',
            lineHeight: '1.8',
          }}
        >
          <li>
            <strong>OpenAI:</strong> AI-powered food analysis and coaching
          </li>
          <li>
            <strong>Fitbit:</strong> Activity, heart rate, and sleep data
            (optional)
          </li>
          <li>
            <strong>Oura Ring:</strong> Sleep, readiness, and activity scores
            (optional)
          </li>
        </ul>
        <p
          className="body-medium"
          style={{
            lineHeight: '1.7',
            color: 'var(--text-secondary)',
            marginTop: '12px',
          }}
        >
          These services operate under their own privacy policies.
        </p>
      </section>

      {/* 6. Data Security */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          6. Data Security
        </h2>
        <p
          className="body-medium"
          style={{ lineHeight: '1.7', color: 'var(--text-secondary)' }}
        >
          We use industry-standard security measures to protect your data.
          However, no method of transmission or storage is 100% secure.
        </p>
      </section>

      {/* 7. Data Retention */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          7. Data Retention
        </h2>
        <p
          className="body-medium"
          style={{ lineHeight: '1.7', color: 'var(--text-secondary)' }}
        >
          We retain your data while your account is active or as needed to
          provide services. You may request account deletion at any time.
        </p>
      </section>

      {/* 8. Your Rights */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          8. Your Rights
        </h2>
        <ul
          style={{
            paddingLeft: '24px',
            color: 'var(--text-secondary)',
            lineHeight: '1.8',
          }}
        >
          <li>Access your personal data</li>
          <li>Correct inaccurate information</li>
          <li>Delete your account and data</li>
          <li>Disconnect wearable integrations</li>
        </ul>
      </section>

      {/* 9. Children */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          9. Children&apos;s Privacy
        </h2>
        <p
          className="body-medium"
          style={{ lineHeight: '1.7', color: 'var(--text-secondary)' }}
        >
          HealthTrail is not intended for children under 13. We do not knowingly
          collect data from children.
        </p>
      </section>

      {/* 10. Contact */}
      <section style={{ marginBottom: '32px' }}>
        <h2 className="heading-4" style={{ marginBottom: '16px' }}>
          10. Contact Us
        </h2>
        <p
          className="body-medium"
          style={{ lineHeight: '1.7', color: 'var(--text-secondary)' }}
        >
          If you have questions about this Privacy Policy, contact us at:
          <br />
          <strong>support@healthtrail.fit</strong>
        </p>
      </section>
    </div>
  );
}

export default PrivacyPolicy;
